import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Context
import { DataSettingsContext, DataSettingsModalContextType } from './context';

// Components
import Modal from "../../../../components/modal";
import DataSettingsModalHeader from "./Header";
import ViewSettings from './Views/Settings';
import ViewAccountManage from "./Views/AccountManage";
import ViewChangeEmail from "./Views/ChangeEmail";
import ViewChangePassword from "./Views/ChangePassword";

// Styles
import styles from "./style.module.scss";

// Types
import { User } from '../../../../types';
import { View } from './types';

interface DataSettingsModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateUser?: (updatedUser: Partial<User>) => void;
}

const DataSettingsModal: React.FC<DataSettingsModalProps> = ({ user, isOpen, onClose, onUpdateUser }) => {
  // =========================== States ===========================
  const [view, setView] = useState<View>('settings');

  // =========================== Header ===========================
  const [headerTitle, setHeaderTitle] = useState<string>('');
  const [headerSubtitle, setHeaderSubtitle] = useState<string>('');

  // Refs
  const verificationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Memoized States
  const needToCreatePassword = useMemo(() => {
    return !user?.has_password;
  }, [user?.has_password]);

  // Effects
  useEffect(() => {
    return () => {
      // Clear verification timer on unmount
      if (verificationTimerRef.current) {
        clearInterval(verificationTimerRef.current);
      }
    };
  }, []);

  // Handlers
  const handleClose = useCallback(() => {
    // Reset States;
    setView('settings');
    setHeaderTitle('');
    setHeaderSubtitle('');

    onClose();
  }, [onClose]);

  // Memoized Context Value
  const contextValue: DataSettingsModalContextType = useMemo(() => ({
    user,
    onClose: handleClose,
    onSetHeaderTitle: setHeaderTitle,
    onSetHeaderSubtitle: setHeaderSubtitle,
    onUpdateUser,
  }), [
    user, handleClose, onUpdateUser
  ]);

  return (
    <DataSettingsContext.Provider value={contextValue}>
      <Modal
        open={isOpen}
        onClose={handleClose}
        title={
          <DataSettingsModalHeader
            title={headerTitle}
            subtitle={headerSubtitle}
            onClose={handleClose}
          />
        }
        classes={{
          content: styles.modal,
        }}
      >
        {view === 'settings' && <ViewSettings createPassword={needToCreatePassword} onViewChange={setView} />}
        {view === 'manage' && <ViewAccountManage />}
        {view === 'change_email' && <ViewChangeEmail />}
        {view === 'change_password' && <ViewChangePassword isCreate={needToCreatePassword} />}
      </Modal>
    </DataSettingsContext.Provider>
  );
};

export default DataSettingsModal;
